import { SortDescriptor } from '@progress/kendo-data-query';
import { SortSettings } from '@progress/kendo-react-data-tools';
import {
  Grid,
  GridColumn,
  GridColumnProps,
  GridDetailRowProps,
  GridPageChangeEvent,
  GridProps,
  GridRowClickEvent,
  GridRowProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import React from 'react';
import styled from 'styled-components';
import CustomPagination from './pagination';
import { TableSkeletonLoader } from '../SkeltonLoaders/TableSkeleton';
import NoDataFound from '../NoDataFound';

type Props = {
  data?: any;
  columns?: Array<GridColumnProps>;
  className?: string;
  wrapperClassName?: string;
  type?:
    | 'coach'
    | 'attendance'
    | 'admin'
    | 'admin-detail'
    | 'parentDashBoard'
    | '';
  noDataLabel?: string;
  showPagination?: boolean;
  loader?: boolean;
  loadingIndicator?: React.ReactNode;
  pageSize?: number;
  skip?: number;
  setSkip?: React.Dispatch<React.SetStateAction<number>>;
  gridPageNum?: number;
  setGridPageNum?: React.Dispatch<React.SetStateAction<number | undefined>>;
  totalCount?: number;
  onRowClick?: (event: GridRowClickEvent) => void;
  onPageChange?: (event: GridPageChangeEvent) => void;
  sort?: SortDescriptor[] | undefined;
  sortable?: SortSettings | undefined;
  onSortChange?: ((event: GridSortChangeEvent) => void) | undefined;
  onBackButtonClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
  isBackButton?: boolean;
  loaderRow?: number;
  noSkeltonTopBorder?: boolean;
  detailComponent?: React.ComponentType<GridDetailRowProps> | null;
  rowRender?: (
    row: React.ReactElement<HTMLTableRowElement>,
    props: GridRowProps,
  ) => React.ReactNode;
  headerChildren?: React.ReactNode; // make the component able to receive (header) children elements
  onPageInputChange?: React.ChangeEventHandler<HTMLInputElement>; //on pagination input change
  onPageSubmit?: (skipValue: number) => void; //on page submit or go button click
};

export interface ProductCategory {
  CategoryID?: number;
  CategoryName?: string;
  Description?: string;
  details?: any;
}

const CustomDataGrid = ({
  data,
  columns,
  className,
  noDataLabel,
  showPagination,
  pageSize,
  skip,
  totalCount,
  loader,
  loadingIndicator,
  onRowClick,
  onPageChange,
  sort,
  sortable,
  onSortChange,
  onBackButtonClick, //might use later
  isBackButton, //might use later
  wrapperClassName,
  type, //might use later
  loaderRow = 10,
  noSkeltonTopBorder,
  detailComponent,
  rowRender,
  headerChildren,
  onPageInputChange,
  onPageSubmit,
  setGridPageNum,
  gridPageNum,
  setSkip,
}: Props) => {
  const scrollbarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const scrollbar = scrollbarRef.current;
    const content = document.querySelector('.k-grid-content') as HTMLElement;

    if (scrollbar && content) {
      const syncScroll = (e: Event) => {
        const source = e.target as HTMLElement;
        if (source === content) {
          scrollbar.scrollLeft = source.scrollLeft;
        } else if (source === scrollbar) {
          content.scrollLeft = source.scrollLeft;
        }
      };

      const updateScrollbarWidth = () => {
        const table = content.querySelector('table');
        if (table) {
          const hasHorizontalScroll = table.offsetWidth > content.offsetWidth;
          scrollbar.style.display = hasHorizontalScroll ? 'block' : 'none';
          scrollbar.style.width = `${content.offsetWidth}px`;
          const innerDiv = scrollbar.querySelector('div') as HTMLElement;
          if (innerDiv) {
            innerDiv.style.width = `${table.offsetWidth}px`;
          }
        }
      };

      // Initial update
      setTimeout(updateScrollbarWidth, 0);

      // Update on resize
      window.addEventListener('resize', updateScrollbarWidth);

      scrollbar.addEventListener('scroll', syncScroll);
      content.addEventListener('scroll', syncScroll);

      return () => {
        window.removeEventListener('resize', updateScrollbarWidth);
        scrollbar.removeEventListener('scroll', syncScroll);
        content.removeEventListener('scroll', syncScroll);
      };
    }
  }, [data, loader]);

  return (
    <CustomGridWrapper className={wrapperClassName ?? ''}>
      {headerChildren && (
        <CustomHeaderWrapper className="custom-header-wrapper">
          {headerChildren}{' '}
        </CustomHeaderWrapper>
      )}
      {loader ? (
        loadingIndicator ?? (
          <TableSkeletonLoader
            column={6}
            row={loaderRow}
            noTopBorder={noSkeltonTopBorder}
          />
        )
      ) : data?.length && !loader ? (
        <>
          <TopScrollbar ref={scrollbarRef}>
            <div></div>
          </TopScrollbar>
          <CustomGrid
            onSortChange={onSortChange}
            sort={sort}
            sortable={sortable}
            onRowClick={onRowClick}
            className={`${className ?? 'CustomGrid'} ${
              showPagination ? 'showPagination' : ''
            }`}
            data={data ?? []}
            total={totalCount}
            skip={skip}
            take={pageSize}
            onPageChange={onPageChange}
            pager={e => (
              <>
                {showPagination && (
                  <CustomPagination
                    props={e}
                    totalCount={totalCount}
                    onPageInputChange={onPageInputChange}
                    gridPageNum={gridPageNum}
                    setGridPageNum={setGridPageNum}
                    setSkip={setSkip}
                    onPageSubmit={onPageSubmit}
                  />
                )}
              </>
            )}
            pageable={{
              info: false,
              previousNext: true,
            }}
            detail={detailComponent}
            rowRender={rowRender}
          >
            {columns?.map((column, index) => {
              return <GridColumn key={index} {...column} />;
            })}
          </CustomGrid>
        </>
      ) : (
        data?.length === 0 &&
        !loader && <NoDataFound noDataLabel={noDataLabel} />
      )}
    </CustomGridWrapper>
  );
};

export default CustomDataGrid;

const TopScrollbar = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  &::-webkit-scrollbar {
    width: 3px;
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: ${p => p.theme.layoutPageScrollColor};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    width: 9px;
    background: ${p => p.theme.layoutPageScrollThumbColor};
    border-radius: 10px;
  }
  > div {
    height: 1px;
  }
`;
const CustomGridWrapper = styled.div`
  border: 1px solid ${p => p.theme.gridCommonBorder};
  border-radius: 8px 8px 8px 8px;
  width: 100%;
  &.chart-not-hidded {
    border-top-right-radius: 0 !important;
  }
`;
const CustomHeaderWrapper = styled.div`
  padding: 16px 25px 14px 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${p => p.theme.tableHeadBorder};
  @media (min-width: 768px) and (max-width: 1023px) {
    padding: 16px 10px 14px 10px;
  }
`;
const CustomGrid = styled(Grid)<GridProps & { children?: React.ReactNode }>`
  background: ${p => p.theme.tableBg};
  border: none;
  border-radius: 14px;
  &.hide-border-bottom {
    .k-grid-container {
      tr {
        &:nth-last-child(1) {
          td {
            border-bottom: 0px;
          }
        }
      }
    }
  }
  &.showPagination {
    .k-grid-container {
      .k-grid-content {
        min-height: auto;
      }
    }
  }
  .k-grid-header {
    table {
      width: 100% !important;
    }
    padding: 0 0 0 0;

    background-color: transparent;
    border-top: 0;
    border-bottom: 1px solid ${p => p.theme.tableHeadBorder};
    .k-grid-header-wrap {
      margin-right: 0px;
      border-width: 0 0 0 0;
    }
    tr {
      th {
        padding: 13px 8px 13px 8px;
        border-width: 0 0 0px 0px;
        height: 47px;
        vertical-align: middle;
        &:nth-child(1) {
          padding: 13px 8px 13px 24px;
        }
        &:nth-last-child(1) {
          padding: 13px 24px 13px 0px;
        }

        .all-check-header {
          .k-link {
            .k-column-title {
              display: flex;
              align-items: center;
              width: 100%;
              input {
                margin: 0 0 0 12px;
              }
            }
          }
        }

        &.k-sorted {
          .k-icon {
            &::before {
              content: url('data:image/svg+xml,%3Csvg%20width%3D%2211%22%20height%3D%2213%22%20viewBox%3D%220%200%2011%2013%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M3%208.9082H0.5L4.25%2012.6582V0.158203H3V8.9082ZM6.75%202.0332V12.6582H8V3.9082H10.5L6.75%200.158203V2.0332Z%22%20fill%3D%22black%22%2F%3E%3C%2Fsvg%3E');
              display: inline-block;
              width: 11px;
              height: 13px;
            }
          }
        }
        .k-cell-inner {
          .k-link {
            font-family: 'Open Sans';
            font-weight: 700;
            font-size: 10px;
            color: ${p => p.theme.tableHeadText};
            cursor: unset;
          }
          .k-icon {
            &::before {
              content: url('data:image/svg+xml,%3Csvg%20width%3D%2211%22%20height%3D%2213%22%20viewBox%3D%220%200%2011%2013%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M3%208.9082H0.5L4.25%2012.6582V0.158203H3V8.9082ZM6.75%202.0332V12.6582H8V3.9082H10.5L6.75%200.158203V2.0332Z%22%20fill%3D%22%23A0AEC0%22%2F%3E%3C%2Fsvg%3E');
              display: inline-block;
              width: 11px;
              height: 13px;
            }
          }
          .k-sort-order {
            display: none;
          }
        }
      }
    }
  }

  .k-grid-container {
    .k-grid-norecords {
      td {
        font-family: 'Open Sans' !important;
        font-weight: 400 !important;
        font-size: 12px !important;
      }
    }
    &:focus-visible {
      outline: none;
    }
    .k-grid-content {
      table {
        width: 100% !important;
        background-color: transparent !important;
      }
      overflow: auto;
      min-height: unset !important;
      &::-webkit-scrollbar {
        width: 3px;
        height: 4px;
      }
      &::-webkit-scrollbar-track {
        background: ${p => p.theme.layoutPageScrollColor};
        border-radius: 10px;
      }
      &::-webkit-scrollbar-thumb {
        width: 9px;
        background: ${p => p.theme.layoutPageScrollThumbColor};
        border-radius: 10px;
      }
      > div {
        outline: none;
      }
      .detail-row-container,
      .k-detail-row {
        transition: height 1s ease;
        .k-detail-cell {
          transition: height 1s ease;
        }
      }
      tbody {
        tr {
          &:last-child {
            border-bottom-right-radius: 14px;
            border-bottom-left-radius: 14px;
            td {
              &:first-child {
                border-bottom-left-radius: 14px !important;
                border-bottom: unset !important;
              }
              &:last-child {
                border-bottom-right-radius: 14px !important;
                border-bottom: unset !important;
              }
            }
          }
          &:nth-of-type(odd) {
            background: transparent;
          }
          &:nth-of-type(even) {
            background: ${p => p.theme.tableTrEvenBg};
          }
          td {
            min-width: 25px;
            height: 40px;
            overflow: hidden;
            text-overflow: ellipsis;
            word-wrap: nowrap;
            &:nth-child(1) {
              padding: 0px 0px 0px 24px;
            }
            &:nth-last-child(1) {
              padding: 0px 32px 0px 0px;
            }
            color: ${p => p.theme.tableCommonText};
            padding: 14px 8px 14px 8px;
            font-family: 'Open Sans';
            font-weight: 600;
            font-size: 12px;
            line-height: 20px;
            background: transparent;
            border-width: 0 0 0 0;
            &.cursor-pointer {
              cursor: pointer;
            }
          }
        }
        tr:last-child td {
          border-bottom: none;
        }
      }
    }
  }
  //Responsive
  @media (min-width: 769px) and (max-width: 991px) {
    .k-grid-header {
      tr {
        th {
          .k-cell-inner {
            .k-link {
              font-size: 9px !important;
            }
          }
        }
      }
    }
    .k-grid-container {
      .k-grid-content {
        tbody {
          tr {
            td {
              font-size: 10px !important;
            }
          }
        }
      }
    }
  }
  @media (min-width: 992px) and (max-width: 1023px) {
    .k-grid-header {
      tr {
        th {
          .k-cell-inner {
            .k-link {
              font-size: 9px !important;
            }
          }
        }
      }
    }
    .k-grid-container {
      .k-grid-content {
        tbody {
          tr {
            td {
              font-size: 10px !important;
            }
          }
        }
      }
    }
  }
  @media (min-width: 1024px) and (max-width: 1279px) {
    .k-grid-header {
      tr {
        th {
          .k-cell-inner {
            .k-link {
              font-size: 9px !important;
            }
          }
        }
      }
    }
    .k-grid-container {
      .k-grid-content {
        tbody {
          tr {
            td {
              font-size: 10px !important;
            }
          }
        }
      }
    }
  }
  @media (min-width: 1280px) and (max-width: 1365px) {
    .k-grid-header {
      tr {
        th {
          .k-cell-inner {
            .k-link {
              font-size: 9px !important;
            }
          }
        }
      }
    }
    .k-grid-container {
      .k-grid-content {
        tbody {
          tr {
            td {
              font-size: 11px !important;
            }
          }
        }
      }
    }
  }
  @media (min-width: 1366px) and (max-width: 1439px) {
    .k-grid-header {
      tr {
        th {
          .k-cell-inner {
            .k-link {
              font-size: 9px !important;
            }
          }
        }
      }
    }
    .k-grid-container {
      .k-grid-content {
        tbody {
          tr {
            td {
              font-size: 11px !important;
            }
          }
        }
      }
    }
  }
  @media (min-width: 1440px) and (max-width: 1600px) {
    .k-grid-header {
      tr {
        th {
          .k-cell-inner {
            .k-link {
              font-size: 9px !important;
            }
          }
        }
      }
    }
    .k-grid-container {
      .k-grid-content {
        tbody {
          tr {
            td {
              font-size: 11px !important;
            }
          }
        }
      }
    }
  }
`;
